/**
 * CSS de la v2 embarqué dans son morceau JS (docs/ux-v2/SPEC.md § 6.2, règle 3).
 *
 * La v2 est chargée à la demande (main.tsx, import dynamique) : un fichier CSS à part serait soit
 * enqueue par Frontend.php pour tous les visiteurs (glob de dist/assets/), soit chargé par l'aide de
 * Vite à une adresse fausse sous WordPress (base « / » : /assets/…, 404). Chaque feuille est donc
 * importée en `?inline` (texte minifié au build) et posée ici, à l'évaluation de son module, donc
 * avant le premier rendu (mountV2) : ni flash sans style, ni requête de plus.
 *
 * Cascade inchangée : quand la v2 était dans le bundle, ses règles suivaient celles de la v1 dans la
 * feuille de l'app (dist/assets/index-<hash>.css). Les balises sont donc posées juste après cette
 * feuille, là où WordPress l'a imprimée (<head> sur monday-2026, pied de page sur Uncode), dans
 * l'ordre où Vite concaténait les fichiers : chaque module pose sa feuille à la place de son ancien
 * `import './x.css'` (toujours son dernier import), dans l'ordre d'évaluation ; une feuille importée
 * deux fois n'est posée qu'une fois, à sa première place. Le « CSS additionnel (v2) » et les styles
 * de Chakra gardent ainsi leur place par rapport à elles. Feuille introuvable : fin du <body>.
 */
const injected = new Set<string>();
let last: Element | null = null;

/**
 * Feuille de l'app imprimée par WordPress : son id est le handle (chemin du fichier) + « -css », même
 * quand FlyingPress déplace le fichier (Dynamo Paris : data-href vers son cache).
 */
function appStylesheet(): Element | null {
  return document.querySelector(
    'link[id*="/apps/client/dist/assets/index-"], link[rel="stylesheet"][href*="/apps/client/dist/assets/index-"]',
  );
}

export function injectV2Css(id: string, css: string): void {
  if (injected.has(id) || typeof document === 'undefined') return;
  injected.add(id);
  const style = document.createElement('style');
  style.setAttribute('data-mmv2-css', id);
  style.textContent = css;
  const anchor = last?.parentNode ? last : appStylesheet();
  if (anchor?.parentNode) anchor.after(style);
  else (document.body ?? document.head).appendChild(style);
  last = style;
}
